import { useEffect, useMemo, useRef, useState } from "react";
import { MessageFeedbackType } from "@/constants/chat.constant";
import { Feedback } from "@/interfaces/chat.interface";
import { ThumbsUpIcon, ThumbsDownIcon } from "lucide-react";
import clsx from "clsx";
import MessageFeedbackInput from "./message-feedback-input-box";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import toast from "react-hot-toast";
import useChatApiService from "@/services/chat.service";
import { debounce, DebouncedFunc } from "lodash";

/**
 * Props for the MessageFeedbackButton component
 */
interface MessageFeedbackProps {
  /** Current feedback data for the message */
  messageFeedback?: Feedback;
  /** Session ID of the conversation */
  sessionId: string;
  /** ID of the message */
  messageId: number;
  /** Type of the conversation: CHOP GPT or Policy Assistant */
  conversationType?: string;
  /** Callback function called when message is liked */
  onLike?: () => void;
  /** Callback function called when message is disliked */
  onDislike?: () => void;
}

/**
 * Determine large or small screen and use popover/dialog accordingly
 * @param query
 * @returns
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    const listener = () => setMatches(media.matches);
    media.addEventListener("change", listener);

    return () => media.removeEventListener("change", listener);
  }, [matches, query]);

  return matches;
}

/**
 * MessageFeedbackButton component displays thumbs up/down buttons for message feedback.
 * Opens a popover (desktop) or dialog (mobile) to collect detailed feedback including
 * tags and text. Uses debounced API calls to save feedback. Disables buttons when
 * detailed feedback has been submitted.
 * 
 * @component
 * @param {MessageFeedbackProps} props - Component props
 * @param {Feedback} [props.messageFeedback] - Current feedback data
 * @param {string} props.sessionId - Session ID
 * @param {number} props.messageId - Message ID
 * @param {() => void} [props.onLike] - Callback fired on like
 * @param {() => void} [props.onDislike] - Callback fired on dislike
 * @returns {JSX.Element} Thumbs up/down buttons with feedback collection UI
 * 
 * @example
 * ```tsx
 * <MessageFeedbackButton 
 *   messageFeedback={feedback}
 *   sessionId="session-123"
 *   messageId={1}
 * />
 * ```
 */
export default function MessageFeedbackButton({
  messageFeedback: initialFeedback,
  sessionId,
  messageId,
  conversationType,
  onLike,
  onDislike,
}: MessageFeedbackProps) {
  const [openPopover, setOpenPopover] = useState<MessageFeedbackType | null>(
    null
  );
  const [messageFeedback, setMessageFeedback] = useState<Feedback | undefined>(
    initialFeedback
  );

  const isLargeScreen = useMediaQuery("(min-width: 768px)");
  const { updateMessageFeedback } = useChatApiService();

  // Map to hold per-message debounce instances
  const debouncedMap = useRef<Record<number, DebouncedFunc<(...args: any[]) => void>>>({});

  // Helper to get or create a per-message debounce
  const getDebounced = (messageId: number) => {
    if (!debouncedMap.current[messageId]) {
      debouncedMap.current[messageId] = debounce(
        async (sessionId: string, messageId: number, request: Feedback) => {
          try {
            await updateMessageFeedback(sessionId, messageId, request);
          } catch (error) {
            console.error("Error sending feedback:", error);
            toast.error("Failed to record feedback");
          } finally {
            //Clean up this debounce after it runs
            delete debouncedMap.current[messageId];
          }
        },
        2000
      );
    }
    return debouncedMap.current[messageId];
  };

  /**
   * Update feedback state if it is updated
   */
  useEffect(() => {
    setMessageFeedback(initialFeedback);
  }, [initialFeedback, messageId]);

  /**
   * Submit Thumbs up or thumbs down selection to the server
   *
   * @param feedbackType
   */
  const sendFeedback = (feedbackType: MessageFeedbackType | null) => {
    const request: Feedback = {
      feedback_type: feedbackType ?? "none",
      feedback_text: {},
    };
    
    const debouncedFn = getDebounced(messageId);
    debouncedFn(sessionId, messageId, request);
  };

  
  /**
   * Submit feedback to server
   * @param updatedFeedback
   */
  const handleFeedbackSubmit = async (updatedFeedback: Feedback) => {
    try {
      // Cancel only this message’s debounce if it exists
      debouncedMap.current[messageId]?.cancel?.();
      setMessageFeedback(updatedFeedback);
      await updateMessageFeedback(sessionId, messageId, updatedFeedback);
      //Cancel any ongoing operation after the user submitted feedback
      debouncedMap.current[messageId]?.cancel?.();
      toast.success("Feedback sent successfully");
    } catch (error) {
      console.error("Error sending feedback:", error);
      toast.error("Failed to record feedback");
    } finally {
      setOpenPopover(null);
      delete debouncedMap.current[messageId]; // cleanup
    }
  };

  /**
   * Flag determines if a particular feedback button is selected
   * @param value
   * @returns
   */
  const isSelected = (value: MessageFeedbackType) =>
    messageFeedback?.feedback_type === value;

  /**
   * Flag which determines if the feedback buttons can be enabled
   * @returns boolean
   */
  const isEnabled = () => {
    if (
      messageFeedback?.feedback_text?.message ||
      (messageFeedback?.feedback_text?.tags &&
        messageFeedback?.feedback_text?.tags.length > 0)
    ) {
      return false;
    }
    return true;
  };

  /**
   * Action when thumbs up or thumbs down icon is clicked
   * @param feedbackType
   * @returns
   */
  const handleButtonClick = async (feedbackType: MessageFeedbackType) => {
    if (!isEnabled()) return;

    if (isSelected(feedbackType)) {
      setMessageFeedback({ feedback_type: "none", feedback_text: {} });
      setOpenPopover(null);
      sendFeedback(null);
    } else {
      setMessageFeedback({ feedback_type: feedbackType, feedback_text: {} });
      setOpenPopover(feedbackType);
      sendFeedback(feedbackType);
    }
  };

  /***
   * Input box content to collect feedback
   */
  const renderFeedbackContent = (feedbackType: MessageFeedbackType) => (
    <MessageFeedbackInput
      feedbackType={feedbackType}
      onFeedbackSent={handleFeedbackSubmit}
      open={openPopover === feedbackType}
      conversationType={conversationType}
    />
  );

  const renderButton = (
    Icon: React.ElementType,
    feedbackType: MessageFeedbackType,
    onClick?: () => void,
    isSelected?: boolean,
    isEnabled: boolean = true
  ) => {
    const feedbackContent = renderFeedbackContent(feedbackType);

    return isLargeScreen ? (
      <Popover
        open={openPopover === feedbackType}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setOpenPopover(null);
          }
        }}
      >
        {/*Popover for desktop*/}
        <PopoverTrigger asChild>
          <div
            onClick={() => handleButtonClick(feedbackType)}
            className={clsx(
              "w-8 h-8 rounded-full flex items-center justify-center text-icon-text-100 mr-2 transition-colors",
              isSelected
                ? "bg-primary-500 text-white"
                : "bg-primary-50 hover:bg-[#e8effa]",
              isEnabled ? "cursor-pointer" : "cursor-not-allowed"
            )}
          >
            <Icon size={14} />
          </div>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="start"
          sideOffset={8}
          className="bg-transparent border-none shadow-none p-0"
        >
          {feedbackContent}
        </PopoverContent>
      </Popover>
    ) : (
      <>
        <div
          onClick={() => handleButtonClick(feedbackType)}
          className={clsx(
            "w-8 h-8 rounded-full flex items-center justify-center text-icon-text-100 mr-2 transition-colors",
            isSelected
              ? "bg-primary-500 text-white"
              : "bg-primary-50 hover:bg-[#e8effa]",
            isEnabled ? "cursor-pointer" : "cursor-not-allowed"
          )}
        >
          <Icon size={14} />
        </div>

        {/*Dialog modal for mobile screens*/}
        <Dialog
          open={openPopover === feedbackType}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setOpenPopover(null);
            }
          }}
        >
          <DialogContent
            className="bg-transparent border-none shadow-none"
            onOpenAutoFocus={(e) => {
              e.preventDefault();
            }}
          >
            {/*For accesibility - hidden in screen*/}
            <DialogTitle className="sr-only">Share your feedback</DialogTitle>

            {feedbackContent}
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return (
    <div className="flex">
      {/*Thumbs up button */}
      {renderButton(
        ThumbsUpIcon,
        MessageFeedbackType.POSITIVE,
        onLike,
        isSelected(MessageFeedbackType.POSITIVE),
        isEnabled()
      )}

      {/*Thumbs down button */}
      {renderButton(
        ThumbsDownIcon,
        MessageFeedbackType.NEGATIVE,
        onDislike,
        isSelected(MessageFeedbackType.NEGATIVE),
        isEnabled()
      )}
    </div>
  );
}
